import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/site/PageHeader";
import { EstimateForm } from "@/components/site/EstimateForm";
import { Phone, Mail, Clock, MapPin } from "lucide-react";

type ContactSearch = { type?: "consultation" };

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Free Painting Estimate — Contact Michael's Painting" },
      { name: "description", content: "Request a free residential or commercial painting estimate in Wisconsin. We respond within 24 business hours." },
      { property: "og:title", content: "Free Painting Estimate — Michael's Painting" },
      { property: "og:description", content: "Request a free residential or commercial painting estimate in Wisconsin." },
      { property: "og:url", content: "https://michaelspaintingwi.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/contact" }],
  }),
  validateSearch: (search: Record<string, unknown>): ContactSearch => ({
    type: search.type === "consultation" ? "consultation" : undefined,
  }),
  component: ContactPage,
});

function ContactPage() {
  const { type } = Route.useSearch();
  const isConsultation = type === "consultation";
  return (
    <>
      <PageHeader
        eyebrow={isConsultation ? "Consultation Request" : "Free Estimate"}
        title={isConsultation ? "Request a painting consultation." : "Let's talk about your project."}
        subtitle={
          isConsultation
            ? "Share your project details, selected colors, and photos — Michael's Painting will review and follow up personally."
            : "Share a few details and we'll schedule an on-site consultation. We respond within 24 business hours."
        }
      />
      <section className="py-24 lg:py-32 bg-[#030303] text-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr,1.3fr] gap-16">
          <div className="space-y-8">
            <div>
              <h2 className="font-serif text-3xl text-white mb-4">Get in touch</h2>
              <p className="text-zinc-400 leading-relaxed">Prefer to talk? Call us directly or send a note — whichever works best for you.</p>
            </div>
            <ul className="space-y-5">
              <li className="flex gap-4"><Phone className="size-5 text-amber-300 mt-0.5" /><div><p className="text-xs uppercase tracking-widest text-zinc-500">Phone</p><a href="tel:6088864598" className="font-serif text-xl text-white hover:text-amber-300 transition-colors">(608) 886-4598</a></div></li>
              <li className="flex gap-4"><Mail className="size-5 text-amber-300 mt-0.5" /><div><p className="text-xs uppercase tracking-widest text-zinc-500">Email</p><a href="mailto:michaelspainting0@gmail.com" className="font-serif text-xl text-white hover:text-amber-300 transition-colors">michaelspainting0@gmail.com</a></div></li>
              <li className="flex gap-4"><Clock className="size-5 text-amber-300 mt-0.5" /><div><p className="text-xs uppercase tracking-widest text-zinc-500">Hours</p><p className="font-serif text-xl text-white">Mon – Fri · 8:00 – 17:00</p></div></li>
              <li className="flex gap-4"><MapPin className="size-5 text-amber-300 mt-0.5" /><div><p className="text-xs uppercase tracking-widest text-zinc-500">Service Area</p><p className="font-serif text-xl text-white">Madison, Sun Prairie, Middleton, Fitchburg, Verona, Waunakee, Stoughton</p></div></li>
            </ul>
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm">
              <p className="text-sm text-zinc-300 leading-relaxed"><span className="font-semibold text-amber-200">Schedule before the next project window fills.</span> Our calendar books out 4–6 weeks in advance during peak season.</p>
            </div>
          </div>
          <EstimateForm variant="page" />
        </div>
      </section>
    </>
  );
}
